import { ancestor, simple } from 'acorn-walk'
import { generate } from 'astring'

import { replaceNode, t } from './ast/utils'

import type { Program } from 'acorn'

export interface CreateCompileCodeOptions {
  async?: boolean
  iife?: boolean
  jsx?: boolean
  module?: boolean
  with?: string
}

/**
 * const React = __interopRequireDefault(require('react'));
 */
const jsxPrefixStatements = [
  t.variableDeclaration('const', [
    t.variableDeclarator(
      t.identifier('React'),
      t.callExpression(t.identifier('__interopRequireDefault'), [
        t.callExpression(t.identifier('require'), [t.literal('react')])
      ])
    )
  ])
]

/**
 * require ??= () => { return {}; };
 * module ??= {};
 * module.exports ??= {};
 * module.exports.__esModule ??= true;
 * function __interopRequireDefault(e) {
 *   return e && e.__esModule ? e.default : e;
 * }
 */
const modulePrefixStatements = [
  t.expressionStatement(
    t.assignmentExpression(
      '??=',
      t.identifier('require'),
      t.arrowFunctionExpression(
        [],
        t.blockStatement([t.returnStatement(t.objectExpression([]))])
      )
    )
  ),
  t.expressionStatement(
    t.assignmentExpression(
      '??=',
      t.identifier('module'),
      t.objectExpression([])
    )
  ),
  t.expressionStatement(
    t.assignmentExpression(
      '??=',
      t.memberExpression(t.identifier('module'), t.identifier('exports')),
      t.objectExpression([])
    )
  ),
  t.expressionStatement(
    t.assignmentExpression(
      '??=',
      t.memberExpression(
        t.identifier('module'),
        t.memberExpression(t.identifier('exports'), t.identifier('__esModule'))
      ),
      t.literal(true)
    )
  ),
  t.functionDeclaration(
    t.identifier('__interopRequireDefault'),
    [t.identifier('e')],
    t.blockStatement([
      t.returnStatement(
        t.conditionalExpression(
          t.logicalExpression(
            '&&',
            t.identifier('e'),
            t.memberExpression(t.identifier('e'), t.identifier('__esModule'))
          ),
          t.memberExpression(t.identifier('e'), t.identifier('default')),
          t.identifier('e')
        )
      )
    ])
  )
]

/**
 * const __r_require = require;
 * const __r_module = module;
 */
const moduleWithObjectPrefixStatements = [
  t.variableDeclaration('const', [
    t.variableDeclarator(t.identifier('__r_require'), t.identifier('require'))
  ]),
  t.variableDeclaration('const', [
    t.variableDeclarator(t.identifier('__r_module'), t.identifier('module'))
  ])
]

/**
 * return function () {
 *   module.function = module.function?.()
 *   return module.exports.default
 * };
 */
const moduleSuffixStatements = [
  t.returnStatement(
    t.functionExpression(
      [],
      t.blockStatement([
        t.expressionStatement(
          t.assignmentExpression(
            '=',
            t.memberExpression(
              t.identifier('module'),
              t.identifier('function')
            ),
            t.chainExpression(
              t.callExpression(
                t.memberExpression(
                  t.identifier('module'),
                  t.identifier('function')
                ),
                [],
                true
              )
            )
          )
        ),
        t.returnStatement(
          t.memberExpression(
            t.identifier('module'),
            t.memberExpression(t.identifier('exports'), t.identifier('default'))
          )
        )
      ])
    )
  )
]

export function createCompileCode(
  code: string,
  codeAst: Program,
  options: CreateCompileCodeOptions = {}
): string {
  let codeBody = codeAst.body

  const wrappedExpression = t.functionExpression(
    [],
    t.blockStatement(codeBody),
    !options.module && options.async
  )
  const wrappedFunction =
    !options.module && options.iife
      ? t.expressionStatement(t.callExpression(wrappedExpression, []))
      : t.expressionStatement(wrappedExpression)

  codeBody = options.module
    ? [
        t.expressionStatement(
          t.assignmentExpression(
            '??=',
            t.memberExpression(
              options.with ? t.identifier('__r_module') : t.identifier('module'),
              t.identifier('function')
            ),
            wrappedFunction
          )
        )
      ]
    : [t.returnStatement(wrappedFunction)]

  if (options.with) {
    if (options.module) {
      ancestor(codeAst, {
        Identifier(node, state, ancestors) {
          if (node.name === 'require') {
            replaceNode(ancestors, t.identifier('__r_require'))
          } else if (node.name === 'module') {
            replaceNode(ancestors, t.identifier('__r_module'))
          }
        }
      })
      codeBody = [
        ...moduleWithObjectPrefixStatements,
        t.withStatement(t.identifier(options.with), t.blockStatement(codeBody))
      ]
    } else {
      codeBody = [
        t.withStatement(t.identifier(options.with), t.blockStatement(codeBody))
      ]
    }
  }

  const compiledAst = t.program(codeBody)

  if (options.jsx) {
    let hasReact = false
    simple(compiledAst, {
      VariableDeclarator(node) {
        if (t.isIdentifier(node.id) && node.id.name === 'React') hasReact = true
      }
    })

    if (!hasReact) compiledAst.body.unshift(...jsxPrefixStatements)
  }

  if (options.module) {
    compiledAst.body.unshift(...modulePrefixStatements)
    compiledAst.body.push(...moduleSuffixStatements)
  }

  return generate(compiledAst)
}
